import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Search, User, Users, X } from 'lucide-react';
import type { CatchballParticipantRef } from '@/types/prioritization/catchballVirtualConfig';
import type { DesignerPath } from '../types';
import { useWorkspaceRoster } from '../hooks/useWorkspaceRoster';

interface CustomPathCanvasProps {
  paths: DesignerPath[];
  activePathId: string;
  /** Label for the fixed level-0 stop (the cycle owner holds the ball first). */
  anchorLabel: string;
  onAddRung: (ref: CatchballParticipantRef) => void;
  onAssignRung: (nodeId: string, ref: CatchballParticipantRef) => void;
  onRemoveRung: (nodeId: string) => void;
  onMoveRung: (nodeId: string, direction: 'up' | 'down') => void;
}

/**
 * The custom-path builder: an ordered ladder of named recipients the ball
 * travels down, for cycles that route past the org chart or run in a workspace
 * that has none. Level 0 is always the cycle owner; every level below is a
 * user or group the designer picks.
 */
export function CustomPathCanvas({
  paths,
  activePathId,
  anchorLabel,
  onAddRung,
  onAssignRung,
  onRemoveRung,
  onMoveRung,
}: CustomPathCanvasProps) {
  const activePath = paths.find((p) => p.id === activePathId);
  const roster = useWorkspaceRoster(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [reassigning, setReassigning] = useState<string | null>(null);

  const rungs = useMemo(() => (activePath?.nodes ?? []).slice(1), [activePath]);
  const color = activePath?.color ?? 'var(--mustard)';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
      <div
        style={{
          height: 40,
          borderBottom: '1px solid var(--rule)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 14px',
          gap: 8,
          background: 'var(--bg-raised)',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: '0.68rem',
            letterSpacing: '0.12em',
            color: 'var(--text-ter)',
            textTransform: 'uppercase',
          }}
        >
          Custom Hierarchy
        </span>
        <span style={{ flex: 1 }} />
        {activePath && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: '0.7rem', color: 'var(--text-sec)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: activePath.color }} />
            {activePath.name}
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 16px' }}>
        <div style={{ maxWidth: 460 }}>
          <LevelLabel text="L0 · Anchor" />
          <div
            style={{
              padding: '10px 12px',
              border: `1px solid ${color}`,
              background: `${color}18`,
            }}
          >
            <div style={{ fontSize: '0.95rem', fontWeight: 500, color: 'var(--text-pri)' }}>{anchorLabel}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-ter)', marginTop: 2 }}>
              Cycle owner — holds the ball first
            </div>
          </div>

          {rungs.map((nodeId, index) => {
            const assignment = activePath?.nodeAssignments?.[nodeId];
            const level = index + 1;
            return (
              <div key={nodeId}>
                <Connector color={color} />
                <LevelLabel text={`L${level}`} />
                <div
                  style={{
                    padding: '10px 12px',
                    border: `1px solid ${assignment ? color : 'var(--coral)'}`,
                    background: assignment ? `${color}10` : 'transparent',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    position: 'relative',
                    zIndex: reassigning === nodeId ? 40 : 1,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {assignment ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {assignment.type === 'group' ? (
                            <Users size={12} style={{ color: 'var(--mustard)', flexShrink: 0 }} />
                          ) : (
                            <User size={12} style={{ color: 'var(--text-ter)', flexShrink: 0 }} />
                          )}
                          <span style={{ fontSize: '0.95rem', fontWeight: 500, color: 'var(--text-pri)' }}>
                            {assignment.displayName}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setReassigning(reassigning === nodeId ? null : nodeId)}
                          style={{
                            marginTop: 4,
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            color: 'var(--text-dim)',
                            textDecoration: 'underline',
                          }}
                        >
                          {assignment.type === 'group' ? 'Group' : 'Person'} — change
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setReassigning(reassigning === nodeId ? null : nodeId)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          color: 'var(--coral)',
                        }}
                      >
                        Pick a person or group →
                      </button>
                    )}

                    {reassigning === nodeId && (
                      <ParticipantPicker
                        users={roster.users}
                        groups={roster.groups}
                        loading={roster.loading}
                        onPick={(ref) => {
                          onAssignRung(nodeId, ref);
                          setReassigning(null);
                        }}
                        onClose={() => setReassigning(null)}
                      />
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                    <IconButton
                      label="Move up"
                      disabled={index === 0}
                      onClick={() => onMoveRung(nodeId, 'up')}
                    >
                      <ChevronUp size={13} />
                    </IconButton>
                    <IconButton
                      label="Move down"
                      disabled={index === rungs.length - 1}
                      onClick={() => onMoveRung(nodeId, 'down')}
                    >
                      <ChevronDown size={13} />
                    </IconButton>
                    <IconButton label="Remove this stop" onClick={() => onRemoveRung(nodeId)}>
                      <X size={13} />
                    </IconButton>
                  </div>
                </div>
              </div>
            );
          })}

          <Connector color={color} />
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setPickerOpen(!pickerOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                width: '100%',
                padding: '10px 12px',
                border: '1px dashed var(--rule-bright)',
                background: 'transparent',
                color: 'var(--text-ter)',
                fontFamily: 'var(--sans)',
                fontSize: '0.88rem',
                cursor: 'pointer',
              }}
            >
              <Plus size={13} />
              Add the next stop
            </button>
            {pickerOpen && (
              <ParticipantPicker
                users={roster.users}
                groups={roster.groups}
                loading={roster.loading}
                onPick={(ref) => {
                  onAddRung(ref);
                  setPickerOpen(false);
                }}
                onClose={() => setPickerOpen(false)}
              />
            )}
          </div>

          {rungs.length === 0 && (
            <p style={{ marginTop: 14, fontSize: '0.95rem', color: 'var(--text-dim)', lineHeight: 1.45 }}>
              Add the people or groups this ball should travel through, in order.
              The first stop receives the opening request; each stop below is
              asked in turn.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function LevelLabel({ text }: { text: string }) {
  return (
    <div
      style={{
        fontFamily: 'var(--mono)',
        fontSize: '0.65rem',
        letterSpacing: '0.14em',
        color: 'var(--text-dim)',
        textTransform: 'uppercase',
        marginBottom: 10,
      }}
    >
      {text}
    </div>
  );
}

function Connector({ color }: { color: string }) {
  return <div style={{ width: 1, height: 18, background: color, opacity: 0.6, margin: '0 0 10px 20px' }} />;
}

function IconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: 3,
        display: 'flex',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? 'var(--rule-bright)' : 'var(--text-dim)',
      }}
    >
      {children}
    </button>
  );
}

/** Searchable user + group picker, shared by "add a stop" and "change". */
function ParticipantPicker({
  users,
  groups,
  loading,
  onPick,
  onClose,
}: {
  users: { id: string; name: string; email: string }[];
  groups: { id: string; name: string }[];
  loading: boolean;
  onPick: (ref: CatchballParticipantRef) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();

  const matchedUsers = users.filter(
    (u) => !term || u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term),
  );
  const matchedGroups = groups.filter((g) => !term || g.name.toLowerCase().includes(term));
  const empty = matchedUsers.length === 0 && matchedGroups.length === 0;

  return (
    <div
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        marginTop: 4,
        width: 260,
        maxHeight: 260,
        overflowY: 'auto',
        background: 'var(--surface)',
        border: '1px solid var(--rule-bright)',
        borderRadius: 2,
        zIndex: 50,
        boxShadow: '0 8px 24px rgba(0,0,0,0.55)',
        isolation: 'isolate',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderBottom: '1px solid var(--rule)' }}>
        <Search size={11} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
          placeholder="Search people and groups…"
          style={{
            flex: 1,
            minWidth: 0,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--text-pri)',
            fontFamily: 'var(--sans)',
            fontSize: '0.78rem',
          }}
        />
      </div>

      {loading ? (
        <div style={{ padding: '10px 12px', fontSize: '0.72rem', color: 'var(--text-dim)' }}>Loading…</div>
      ) : empty ? (
        <div style={{ padding: '10px 12px', fontSize: '0.72rem', color: 'var(--text-dim)' }}>
          No people or groups match.
        </div>
      ) : (
        <>
          {matchedUsers.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => onPick({ type: 'user', id: u.id, displayName: u.name || u.email })}
              style={pickerRowStyle}
            >
              <span style={{ fontSize: '0.78rem', color: 'var(--text-pri)' }}>{u.name || u.email}</span>
              {u.name && <span style={{ fontSize: '0.65rem', color: 'var(--text-dim)' }}>{u.email}</span>}
            </button>
          ))}
          {matchedGroups.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => onPick({ type: 'group', id: g.id, displayName: g.name })}
              style={{ ...pickerRowStyle, flexDirection: 'row', alignItems: 'center', gap: 6 }}
            >
              <Users size={11} style={{ color: 'var(--mustard)', flexShrink: 0 }} />
              <span style={{ fontSize: '0.78rem', color: 'var(--text-pri)' }}>{g.name}</span>
            </button>
          ))}
        </>
      )}
    </div>
  );
}

const pickerRowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  textAlign: 'left',
  padding: '7px 10px',
  background: 'var(--surface)',
  border: 'none',
  borderBottom: '1px solid var(--rule)',
  cursor: 'pointer',
};
